import React, { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import startupService from '../../services/startupService';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    Add as AddIcon,
    Save as SaveIcon,
    Close as CloseIcon,
} from '@mui/icons-material';
import Navbar from '../../components/Navbar';
import Sidebar from '../../components/Sidebar';

// ==================== COLORS & THEME ====================
const COLORS = {
    primary: '#0D47A1',
    secondary: '#D32F2F',
    success: '#4CAF50',
    warning: '#FF9800',
    light: '#F5F7FA',
    text: '#263238',
    border: '#E0E0E0',
};

// ==================== TAB PANEL COMPONENT ====================
function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

// ==================== MAIN COMPONENT ====================
export default function MyStartup() {
    // ==================== THIS IS THE KEY FIX - STEP 1 ====================
    const { user, userProfile, updateUserProfile } = useAuth(); // ← Added updateUserProfile
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [tabValue, setTabValue] = useState(0);

    // ==================== STARTUP FORM STATE ====================
    const [startupForm, setStartupForm] = useState({
        name: '',
        founder: '',
        category: '',
        stage: 'Idea',
        status: 'Active',
        email: '',
        phone: '',
        websiteUrl: '',
        linkedinUrl: '',
        problemStatement: '',
        solutionSummary: '',
        technologyStack: '',
    });

    // ==================== TEAM FORM STATE ====================
    const [teamForm, setTeamForm] = useState({
        name: '',
        position: '',
        email: '',
        phone: '',
        yearsOfExperience: 0,
    });

    // ==================== UI STATE ====================
    const [startupId, setStartupId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editingStartup, setEditingStartup] = useState(false);
    const [teamMembers, setTeamMembers] = useState([]);
    const [openTeamDialog, setOpenTeamDialog] = useState(false);
    const [editingTeamMember, setEditingTeamMember] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // ==================== LOAD STARTUP DATA FROM FIRESTORE ====================
    useEffect(() => {
        const loadStartup = async () => {
            // Wait for userProfile to be ready
            if (!userProfile) {
                // console.log('⏳ Waiting for userProfile to load...');
                return;
            }

            try {
                setLoading(true);
                setError('');

                // Check if user has a startup
                if (userProfile?.startupId) {
                    // console.log('📥 Loading startup with ID:', userProfile.startupId);

                    // FETCH FROM FIRESTORE
                    const startup = await startupService.getStartupById(userProfile.startupId);

                    if (startup && startup.id) {
                        // Update form with fetched data
                        setStartupForm({
                            name: startup.name || '',
                            founder: startup.founder || '',
                            category: startup.category || '',
                            stage: startup.stage || 'Idea',
                            status: startup.status || 'Active',
                            email: startup.email || '',
                            phone: startup.phone || '',
                            websiteUrl: startup.websiteUrl || '',
                            linkedinUrl: startup.linkedinUrl || '',
                            problemStatement: startup.problemStatement || '',
                            solutionSummary: startup.solutionSummary || '',
                            technologyStack: startup.technologyStack || '',
                        });

                        // Save startup ID
                        setStartupId(startup.id);

                        // Fetch team members
                        const members = await startupService.getTeamMembers(startup.id);
                        setTeamMembers(members || []);

                        // Switch to VIEW MODE
                        setEditingStartup(false);

                        // console.log('✅ Startup loaded successfully from Firestore');
                    } else {
                        // console.log('⚠️ Startup not found in Firestore');
                        setError('Startup data not found in database');
                    }
                } else {
                    // console.log('📝 User has no startup yet - showing empty form');
                    // User doesn't have a startup - show empty form
                    setEditingStartup(true);
                }
            } catch (err) {
                console.error('❌ Error loading startup:', err);
                setError(`Failed to load startup: ${err.message}`);
                setEditingStartup(true);
            } finally {
                setLoading(false);
            }
        };

        loadStartup();
    }, [userProfile]);

    // ==================== FUNCTION TO REFRESH STARTUP DATA ====================
    const refreshStartupData = async () => {
        if (!startupId) return;

        try {
            // console.log('🔄 Refreshing startup data from Firestore...');

            // Fetch latest startup data
            const startup = await startupService.getStartupById(startupId);
            setStartupForm({
                name: startup.name || '',
                founder: startup.founder || '',
                category: startup.category || '',
                stage: startup.stage || 'Idea',
                status: startup.status || 'Active',
                email: startup.email || '',
                phone: startup.phone || '',
                websiteUrl: startup.websiteUrl || '',
                linkedinUrl: startup.linkedinUrl || '',
                problemStatement: startup.problemStatement || '',
                solutionSummary: startup.solutionSummary || '',
                technologyStack: startup.technologyStack || '',
            });

            // Fetch latest team members
            const members = await startupService.getTeamMembers(startupId);
            setTeamMembers(members || []);

            // console.log('✅ Data refreshed successfully');
        } catch (err) {
            console.error('❌ Error refreshing startup data:', err);
        }
    };

    // ==================== STARTUP FORM HANDLERS ====================
    const handleStartupChange = (e) => {
        const { name, value } = e.target;
        setStartupForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // ==================== THIS IS THE KEY FIX - STEP 2 ====================
    const saveStartup = async () => {
        setError('');
        setSuccess('');

        // Validation
        if (!startupForm.name || !startupForm.email) {
            setError('❌ Startup name and email are required');
            return;
        }

        try {
            setLoading(true);

            if (startupId) {
                // ==================== UPDATE EXISTING STARTUP ====================
                // console.log('📤 Updating startup...');
                await startupService.updateStartup(startupId, startupForm);
                setSuccess('✅ Startup updated successfully!');
                // console.log('✅ Startup update complete');
            } else {
                // ==================== CREATE NEW STARTUP ====================
                // console.log('📤 Creating new startup...');
                const created = await startupService.createStartup({
                    ...startupForm,
                    ownerUid: user?.uid || null,
                });

                // console.log('✅ Startup created in Firestore:', created.id);

                // ==================== CRITICAL FIX: Save startupId to user profile ====================
                if (created?.id && user?.uid) {
                    // console.log('💾 Saving startupId to user profile...');
                    try {
                        // THIS IS THE MISSING LINE - STEP 2 OF THE FIX
                        await updateUserProfile({ startupId: created.id });
                        // console.log('✅ startupId saved to user profile:', created.id);
                        setStartupId(created.id);
                    } catch (err) {
                        console.error('❌ Error saving startupId to profile:', err);
                        setError(`❌ Failed to link startup to profile: ${err.message}`);
                        // Still set it locally so user can continue
                        setStartupId(created.id);
                    }
                }

                setSuccess('✅ Startup created successfully!');
            }

            // Refresh data from Firestore after save
            // console.log('🔄 Refreshing startup data...');
            await refreshStartupData();

            // Switch to view mode
            setEditingStartup(false);
            // console.log('✅ Switched to view mode');
        } catch (err) {
            console.error('❌ Error saving startup:', err);
            setError(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ==================== TEAM MEMBER HANDLERS ====================
    const handleTeamFormChange = (e) => {
        const { name, value } = e.target;
        setTeamForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const openAddTeamDialog = () => {
        setEditingTeamMember(null);
        setTeamForm({
            name: '',
            position: '',
            email: '',
            phone: '',
            yearsOfExperience: 0,
        });
        setOpenTeamDialog(true);
    };

    const openEditTeamDialog = (member) => {
        setEditingTeamMember(member);
        setTeamForm({
            name: member.name || '',
            position: member.position || '',
            email: member.email || '',
            phone: member.phone || '',
            yearsOfExperience: member.yearsOfExperience || 0,
        });
        setOpenTeamDialog(true);
    };

    const saveTeamMember = async () => {
        setError('');

        // Validation
        if (!teamForm.name || !teamForm.position) {
            setError('❌ Name and position are required');
            return;
        }

        try {
            setLoading(true);

            if (editingTeamMember?.id) {
                // UPDATE existing team member
                // console.log('📤 Updating team member...');
                await startupService.updateTeamMember(editingTeamMember.id, teamForm);

                // Refresh team members list
                await refreshStartupData();

                setSuccess('✅ Team member updated!');
            } else {
                // ADD new team member
                // console.log('📤 Adding new team member...');
                await startupService.addTeamMember(startupId, teamForm);

                // Refresh team members list
                await refreshStartupData();

                setSuccess('✅ Team member added!');
            }

            setOpenTeamDialog(false);
        } catch (err) {
            console.error('❌ Error saving team member:', err);
            setError(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const deleteTeamMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to delete this team member?')) return;

        try {
            setLoading(true);
            // console.log('🗑️ Deleting team member...');
            await startupService.deleteTeamMember(startupId, memberId);

            // Refresh team members list
            await refreshStartupData();

            setSuccess('✅ Team member deleted!');
        } catch (err) {
            console.error('❌ Error deleting team member:', err);
            setError(`❌ ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // ==================== RENDER ====================
    if (loading && startupId) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', height: '100vh', bg: COLORS.light }}>
            {/* SIDEBAR */}
            <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

            {/* MAIN CONTENT */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* NAVBAR */}
                <Navbar onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

                {/* SCROLLABLE CONTENT */}
                <Box
                    sx={{
                        flex: 1,
                        overflow: 'auto',
                        p: 3,
                        backgroundColor: COLORS.light,
                    }}
                >
                    <Container maxWidth="lg">
                        {/* HEADER */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h4" sx={{ fontWeight: 'bold', color: COLORS.text }}>
                                🚀 My Startup
                            </Typography>
                            <Typography variant="body2" sx={{ color: COLORS.text, mt: 1 }}>
                                Manage your startup details and team members
                            </Typography>
                        </Box>

                        {/* ALERTS */}
                        {error && (
                            <Alert severity="error" onClose={() => setError('')} sx={{ mb: 3 }}>
                                {error}
                            </Alert>
                        )}
                        {success && (
                            <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 3 }}>
                                {success}
                            </Alert>
                        )}

                        {/* TABS */}
                        <Paper sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden' }}>
                            <Tabs
                                value={tabValue}
                                onChange={(e, newValue) => setTabValue(newValue)}
                                sx={{
                                    backgroundColor: 'white',
                                    borderBottom: `2px solid ${COLORS.border}`,
                                    '& .MuiTab-root': {
                                        textTransform: 'none',
                                        fontSize: '16px',
                                        fontWeight: 500,
                                    },
                                    '& .Mui-selected': {
                                        color: COLORS.primary,
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: COLORS.primary,
                                    },
                                }}
                            >
                                <Tab label="📋 Startup Details" id="tab-0" />
                                <Tab label="👥 Team Members" id="tab-1" />
                            </Tabs>

                            {/* ==================== TAB 1: STARTUP DETAILS ==================== */}
                            <TabPanel value={tabValue} index={0}>
                                {editingStartup ? (
                                    // EDIT MODE
                                    <Box component="form">
                                        <Grid container spacing={3}>
                                            {/* Row 1: Name & Founder */}
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Startup Name *"
                                                    name="name"
                                                    value={startupForm.name}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Founder Name"
                                                    name="founder"
                                                    value={startupForm.founder}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>

                                            {/* Row 2: Category & Stage */}
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Category"
                                                    name="category"
                                                    value={startupForm.category}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    select
                                                    label="Stage"
                                                    name="stage"
                                                    value={startupForm.stage}
                                                    onChange={handleStartupChange}
                                                    SelectProps={{ native: true }}
                                                    variant="outlined"
                                                >
                                                    <option value="Idea">Idea</option>
                                                    <option value="MVP">MVP</option>
                                                    <option value="Growth">Growth</option>
                                                    <option value="Scale">Scale</option>
                                                </TextField>
                                            </Grid>

                                            {/* Row 3: Email & Phone */}
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Email *"
                                                    name="email"
                                                    type="email"
                                                    value={startupForm.email}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Phone"
                                                    name="phone"
                                                    value={startupForm.phone}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>

                                            {/* Row 4: Website & LinkedIn */}
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Website URL"
                                                    name="websiteUrl"
                                                    value={startupForm.websiteUrl}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="LinkedIn URL"
                                                    name="linkedinUrl"
                                                    value={startupForm.linkedinUrl}
                                                    onChange={handleStartupChange}
                                                    variant="outlined"
                                                />
                                            </Grid>

                                            {/* Row 5: Problem Statement */}
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Problem Statement"
                                                    name="problemStatement"
                                                    value={startupForm.problemStatement}
                                                    onChange={handleStartupChange}
                                                    multiline
                                                    rows={4}
                                                    variant="outlined"
                                                />
                                            </Grid>

                                            {/* Row 6: Solution Summary */}
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Solution Summary"
                                                    name="solutionSummary"
                                                    value={startupForm.solutionSummary}
                                                    onChange={handleStartupChange}
                                                    multiline
                                                    rows={4}
                                                    variant="outlined"
                                                />
                                            </Grid>

                                            {/* Row 7: Technology Stack */}
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Technology Stack"
                                                    name="technologyStack"
                                                    value={startupForm.technologyStack}
                                                    onChange={handleStartupChange}
                                                    multiline
                                                    rows={3}
                                                    variant="outlined"
                                                    helperText="Comma separated: React, Node.js, Firebase, etc."
                                                />
                                            </Grid>

                                            {/* BUTTONS */}
                                            <Grid item xs={12} sx={{ display: 'flex', gap: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<SaveIcon />}
                                                    onClick={saveStartup}
                                                    disabled={loading}
                                                    sx={{
                                                        backgroundColor: COLORS.primary,
                                                        '&:hover': { backgroundColor: '#0A3A7C' },
                                                    }}
                                                >
                                                    {loading ? 'Saving...' : 'Save Startup'}
                                                </Button>
                                                {startupId && (
                                                    <Button
                                                        variant="outlined"
                                                        onClick={() => setEditingStartup(false)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                )}
                                            </Grid>
                                        </Grid>
                                    </Box>
                                ) : (
                                    // VIEW MODE
                                    <Box>
                                        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                Your Startup Profile
                                            </Typography>
                                            <Button
                                                variant="contained"
                                                startIcon={<EditIcon />}
                                                onClick={() => setEditingStartup(true)}
                                                sx={{
                                                    backgroundColor: COLORS.primary,
                                                    '&:hover': { backgroundColor: '#0A3A7C' },
                                                }}
                                            >
                                                Edit
                                            </Button>
                                        </Box>

                                        <Grid container spacing={2}>
                                            {/* Cards for display */}
                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Startup Name
                                                        </Typography>
                                                        <Typography variant="h6">{startupForm.name || 'Not specified'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Founder
                                                        </Typography>
                                                        <Typography variant="h6">{startupForm.founder || 'Not specified'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Category
                                                        </Typography>
                                                        <Typography variant="h6">{startupForm.category || 'Not specified'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Stage
                                                        </Typography>
                                                        <Chip
                                                            label={startupForm.stage}
                                                            color={startupForm.stage === 'Scale' ? 'success' : 'primary'}
                                                        />
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Email
                                                        </Typography>
                                                        <Typography variant="h6">{startupForm.email || 'Not specified'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <Card sx={{ h: '100%' }}>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Phone
                                                        </Typography>
                                                        <Typography variant="h6">{startupForm.phone || 'Not specified'}</Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Problem Statement
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {startupForm.problemStatement || 'Not specified'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Solution Summary
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {startupForm.solutionSummary || 'Not specified'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>

                                            <Grid item xs={12}>
                                                <Card>
                                                    <CardContent>
                                                        <Typography color="textSecondary" gutterBottom>
                                                            Technology Stack
                                                        </Typography>
                                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {startupForm.technologyStack || 'Not specified'}
                                                        </Typography>
                                                    </CardContent>
                                                </Card>
                                            </Grid>
                                        </Grid>
                                    </Box>
                                )}
                            </TabPanel>

                            {/* ==================== TAB 2: TEAM MEMBERS ==================== */}
                            <TabPanel value={tabValue} index={1}>
                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                        Team Members ({teamMembers.length})
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={openAddTeamDialog}
                                        sx={{
                                            backgroundColor: COLORS.success,
                                            '&:hover': { backgroundColor: '#45a049' },
                                        }}
                                    >
                                        Add Member
                                    </Button>
                                </Box>

                                {teamMembers.length === 0 ? (
                                    <Alert severity="info">No team members added yet. Click "Add Member" to get started!</Alert>
                                ) : (
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead sx={{ backgroundColor: COLORS.light }}>
                                                <TableRow>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Position</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                                                    <TableCell sx={{ fontWeight: 'bold' }}>Experience (Yrs)</TableCell>
                                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                                                        Actions
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {teamMembers.map((member) => (
                                                    <TableRow key={member.id} hover>
                                                        <TableCell>{member.name}</TableCell>
                                                        <TableCell>{member.position}</TableCell>
                                                        <TableCell>{member.email}</TableCell>
                                                        <TableCell>{member.phone || '-'}</TableCell>
                                                        <TableCell>{member.yearsOfExperience || '-'}</TableCell>
                                                        <TableCell align="center">
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => openEditTeamDialog(member)}
                                                                sx={{ color: COLORS.primary }}
                                                            >
                                                                <EditIcon />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => deleteTeamMember(member.id)}
                                                                sx={{ color: COLORS.secondary }}
                                                            >
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </TabPanel>
                        </Paper>
                    </Container>
                </Box>
            </Box>

            {/* ==================== TEAM MEMBER DIALOG ==================== */}
            <Dialog open={openTeamDialog} onClose={() => setOpenTeamDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ backgroundColor: COLORS.primary, color: 'white' }}>
                    {editingTeamMember ? '✏️ Edit Team Member' : '➕ Add Team Member'}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <TextField
                        fullWidth
                        label="Name *"
                        name="name"
                        value={teamForm.name}
                        onChange={handleTeamFormChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Position *"
                        name="position"
                        value={teamForm.position}
                        onChange={handleTeamFormChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Email"
                        name="email"
                        type="email"
                        value={teamForm.email}
                        onChange={handleTeamFormChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Phone"
                        name="phone"
                        value={teamForm.phone}
                        onChange={handleTeamFormChange}
                        margin="normal"
                    />
                    <TextField
                        fullWidth
                        label="Years of Experience"
                        name="yearsOfExperience"
                        type="number"
                        value={teamForm.yearsOfExperience}
                        onChange={handleTeamFormChange}
                        margin="normal"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setOpenTeamDialog(false)}>Cancel</Button>
                    <Button
                        onClick={saveTeamMember}
                        variant="contained"
                        sx={{ backgroundColor: COLORS.primary }}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : editingTeamMember ? 'Update' : 'Add'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}